import type {
  CustomDashboardTabLayout,
  ManagedCustomDashboardTabsConfig,
} from '@/lib/api/custom-dashboard';

/** 設定画面で扱うグループ 1 つ分の並び（タブは表示順の tabId）。 */
export interface TabLayoutGroup {
  groupId: string;
  tabIds: string[];
}

/** 設定画面の配置。 */
export interface TabLayout {
  groups: TabLayoutGroup[];
  /** グループ未指定のタブ（閲覧画面に出ない。BE が返す作成順）。 */
  ungroupedTabIds: string[];
}

/**
 * 設定一覧から配置を作る。BE は `tabs` をグループの順 → グループ内の順に返し、グループ未指定のタブを末尾に返すため、
 * その順を保つ。消えたグループを指すタブもグループ未指定として扱う。
 */
export function layoutFromConfig(config: ManagedCustomDashboardTabsConfig): TabLayout {
  const groups: TabLayoutGroup[] = config.groups.map((group) => ({
    groupId: group.groupId,
    tabIds: [],
  }));
  const byId = new Map(groups.map((group) => [group.groupId, group]));
  const ungroupedTabIds: string[] = [];
  for (const tab of config.tabs) {
    const group = tab.groupId === null ? undefined : byId.get(tab.groupId);
    if (group) group.tabIds.push(tab.tabId);
    else ungroupedTabIds.push(tab.tabId);
  }
  return { groups, ungroupedTabIds };
}

function swap<T>(items: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (index < 0 || target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

function withoutTab(layout: TabLayout, tabId: string): TabLayout {
  return {
    groups: layout.groups.map((group) => ({
      ...group,
      tabIds: group.tabIds.filter((id) => id !== tabId),
    })),
    ungroupedTabIds: layout.ungroupedTabIds.filter((id) => id !== tabId),
  };
}

/** グループを上（-1）・下（+1）へ 1 つ動かす。 */
export function moveGroup(layout: TabLayout, groupId: string, delta: -1 | 1): TabLayout {
  return {
    ...layout,
    groups: swap(
      layout.groups,
      layout.groups.findIndex((group) => group.groupId === groupId),
      delta,
    ),
  };
}

/** タブをグループ内で上（-1）・下（+1）へ 1 つ動かす（グループ未指定のタブは順を持たないため動かさない）。 */
export function moveTab(layout: TabLayout, tabId: string, delta: -1 | 1): TabLayout {
  return {
    ...layout,
    groups: layout.groups.map((group) =>
      group.tabIds.includes(tabId)
        ? { ...group, tabIds: swap(group.tabIds, group.tabIds.indexOf(tabId), delta) }
        : group,
    ),
  };
}

/** タブを別のグループの末尾へ移す。`targetGroupId` が null ならグループ未指定にする。 */
export function moveTabToGroup(
  layout: TabLayout,
  tabId: string,
  targetGroupId: string | null,
): TabLayout {
  const rest = withoutTab(layout, tabId);
  if (targetGroupId === null) {
    return { ...rest, ungroupedTabIds: [...rest.ungroupedTabIds, tabId] };
  }
  return {
    ...rest,
    groups: rest.groups.map((group) =>
      group.groupId === targetGroupId ? { ...group, tabIds: [...group.tabIds, tabId] } : group,
    ),
  };
}

/** 並べ替え・移動した配置を保存する body（削除なし）。 */
export function layoutRequest(layout: TabLayout): CustomDashboardTabLayout {
  return { ...layout, deleteGroupIds: [], deleteTabIds: [] };
}

/** タブを削除する body。 */
export function deleteTabRequest(layout: TabLayout, tabId: string): CustomDashboardTabLayout {
  return { ...withoutTab(layout, tabId), deleteGroupIds: [], deleteTabIds: [tabId] };
}

/** グループを削除する body。中のタブはグループ未指定にする。 */
export function deleteGroupRequest(layout: TabLayout, groupId: string): CustomDashboardTabLayout {
  const removed = layout.groups.find((group) => group.groupId === groupId);
  return {
    groups: layout.groups.filter((group) => group.groupId !== groupId),
    ungroupedTabIds: [...layout.ungroupedTabIds, ...(removed?.tabIds ?? [])],
    deleteGroupIds: [groupId],
    deleteTabIds: [],
  };
}
